import { useState } from "react";

import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "@/components/marketing/MarketingSection";
import LoopVideo from "@/components/marketing/motion/LoopVideo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

const DIMENSIONS = [
  {
    name: "Apple Music (1:1)",
    size: "3840 × 3840",
    border: "border-[#FF8E97]",
  },
  {
    name: "Apple Music (3:4)",
    size: "2048 × 2732",
    border: "border-[#FF8E97]",
  },
  { name: "Spotify Canvas", size: "1080 × 1920", border: "border-[#6FD28B]" },
];

const FORMATS = [
  {
    name: "Apple (Square)",
    size: "3840 × 3840",
    label: "Apple Music square motion artwork preview",
    mask: "[--side-mask:0%]",
  },
  {
    name: "Apple (Portrait)",
    size: "2048 × 2732",
    label: "Apple Music portrait motion artwork preview",
    mask: "[--side-mask:12.5%]",
  },
  {
    name: "Spotify Canvas",
    size: "1080 × 1920",
    label: "Spotify Canvas motion artwork preview",
    mask: "[--side-mask:21.875%]",
  },
];

const TAB_CLASS =
  "flex min-h-[76px] min-w-0 cursor-pointer flex-col items-center justify-center gap-[5px] rounded-[12px] border px-2 py-[11px] text-center font-figtree text-[12px] font-extrabold leading-[1.15] transition-[border-color,background,color,transform] duration-[180ms] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[rgba(49,67,238,0.22)] motion-safe:hover:-translate-y-px max-[680px]:px-[5px] max-[680px]:py-[10px] max-[680px]:text-[10px]";

const PREVIEW_CLASS =
  "relative mt-4 grid aspect-square w-full place-items-center overflow-hidden rounded-[8px] border border-[#242936] bg-black before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-[2] before:w-[var(--side-mask)] before:bg-black before:content-[''] after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-[2] after:w-[var(--side-mask)] after:bg-black after:content-[''] motion-safe:before:transition-[width] motion-safe:before:duration-[240ms] motion-safe:after:transition-[width] motion-safe:after:duration-[240ms]";

export default function MotionPreview() {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [active, setActive] = useState(FORMATS[0]);

  return (
    <MarketingSection
      className="bg-[#F6F7FB]"
      innerClassName="grid grid-cols-[minmax(0,1.12fr)_minmax(420px,0.88fr)] items-center gap-[72px] max-[980px]:grid-cols-1 max-[980px]:gap-[46px]"
    >
      <div>
        <SectionKicker>Preview and export</SectionKicker>
        <SectionTitle>Preview it before you export.</SectionTitle>
        <SectionIntro className="!max-w-[620px]">
          Lutely creates subtle, looping animation with Apple Music and
          Spotify’s motion guidelines in mind. Preview each format, then export
          high-resolution files prepared to each platform’s technical
          specifications.
        </SectionIntro>
        <h3 className="mb-[10px] mt-7 max-w-[510px] font-figtree text-[18px] font-[850] leading-[1.25] text-marketing-ink">
          Export dimensions
        </h3>
        <ul className="m-0 grid max-w-[510px] list-none gap-[10px] p-0 font-open-sans text-[16px] leading-[1.55]">
          {DIMENSIONS.map((item) => (
            <li
              key={item.name}
              className={cn(
                "flex items-center justify-between gap-[18px] rounded-[14px] border bg-white px-[17px] py-[15px] max-[680px]:flex-col max-[680px]:items-start max-[680px]:gap-[6px]",
                item.border,
              )}
            >
              <strong className="font-figtree text-[15px] font-extrabold leading-[1.2]">
                {item.name}
              </strong>
              <span className="text-[14px] font-[850] text-blue-600">
                {item.size}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div
        role="group"
        aria-label="Interactive Motion Artwork format preview"
        className="w-full max-w-[520px] justify-self-end overflow-hidden rounded-[20px] border border-[#DCE1EE] bg-[#F4F5F8] p-5 max-[680px]:rounded-[18px] max-[680px]:p-3"
      >
        <div
          role="group"
          aria-label="Choose a motion artwork format"
          className="grid grid-cols-3 gap-[10px] max-[680px]:gap-[6px]"
        >
          {FORMATS.map((format) => {
            const selected = format === active;
            return (
              <button
                key={format.name}
                type="button"
                aria-pressed={selected}
                onClick={() => setActive(format)}
                className={cn(
                  TAB_CLASS,
                  selected
                    ? "border-blue-600 bg-[#EEF1FF] text-blue-600"
                    : "border-[#CFD5E2] bg-white text-[#292D37] hover:border-[#A9B2C9]",
                )}
              >
                <strong>{format.name}</strong>
                <small className="font-open-sans text-[11px] font-semibold leading-[1.1] text-[#626977] max-[680px]:text-[10px]">
                  {format.size}
                </small>
              </button>
            );
          })}
        </div>
        <div className={cn(PREVIEW_CLASS, active.mask)}>
          <LoopVideo
            src="/marketing/motion/motion-square.mp4"
            poster="/marketing/motion/motion-square.webp"
            playing={!reduceMotion}
            aria-label={active.label}
            className="block size-full object-cover"
          />
        </div>
      </div>
    </MarketingSection>
  );
}
